<script setup lang="ts">
/**
 * Gift 13 Preview (3222:19635; mobile 3222:107642) — AIRA speaks as the consultant who
 * picked the gifts: "Picked by AIRA", a first-person title with the number of ideas, the
 * answers as chips, three cards and a "+N more ideas" tile (mobile: the row scrolls). No
 * Back — this is a result. Save to list / See all N ideas.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairQuizGlassPill } from '@/ui/patterns/FairQuiz'
import { FairProductCardV2 } from '@/ui/patterns/FairProductCardV2'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairChip } from '@/ui/primitives/FairChip'
import type { FairSearchV2ProductCard } from '@/widgets/search/fair-v2/fair-search-v2.data'
import { GIFT_QUIZ_PREVIEW_COUNT } from '../gift-quiz.data'

const props = defineProps<{
  name: string
  chips: string[]
  cards: FairSearchV2ProductCard[]
  compact: boolean
  saving: boolean
}>()

const emit = defineEmits<{
  save: []
  'see-all': []
}>()

const { t } = useI18n()

const shown = computed(() => (props.compact ? props.cards : props.cards.slice(0, GIFT_QUIZ_PREVIEW_COUNT)))
const more = computed(() => Math.max(0, props.cards.length - GIFT_QUIZ_PREVIEW_COUNT))
</script>

<template>
  <div class="fair-quiz-stage fair-quiz-stage--fill">
    <section class="fair-quiz-card gift-quiz-preview">
      <div class="fair-quiz-card__head">
        <div class="gift-quiz-preview__tag">
          <FairQuizGlassPill :label="t('gift_quiz.preview.pill')" />
        </div>
        <h2 class="fair-quiz-card__title">
          {{ t('gift_quiz.preview.title', { count: cards.length, name }, cards.length) }}
        </h2>
        <p class="fair-quiz-card__subtitle">
          {{ t('gift_quiz.preview.subtitle') }}
        </p>
      </div>

      <div class="fair-quiz-card__content gift-quiz-preview__content">
        <div class="gift-quiz-preview__chips">
          <FairChip
            v-for="chip in chips"
            :key="chip"
            :label="chip"
            :size="compact ? 'mobile' : 'default'"
          />
        </div>
        <div class="gift-quiz-preview__cards">
          <div
            v-for="card in shown"
            :key="`${card.id}-${card.variantId}`"
            class="gift-quiz-preview__card"
          >
            <FairProductCardV2
              :brand="card.brand"
              :title="card.title"
              :price="card.price"
              :images="card.images"
              :colors="card.colors"
              :colors-total="card.colorsTotal"
              :from-label="t('gift_quiz.preview.from')"
              :interactive="false"
              size="mobile"
            />
          </div>
          <div
            v-if="!compact && more"
            class="gift-quiz-preview__more"
          >
            <span class="gift-quiz-preview__more-count">{{ t('gift_quiz.preview.more_count', { count: more }) }}</span>
            <span class="gift-quiz-preview__more-text">{{ t('gift_quiz.preview.more') }}</span>
          </div>
        </div>
      </div>

      <div class="fair-quiz-card__footer gift-quiz-preview__footer">
        <FairButton
          class="gift-quiz-preview__save"
          variant="tonal"
          size="auto"
          :label="t('gift_quiz.preview.save')"
          :loading="saving"
          @click="emit('save')"
        />
        <FairButton
          class="gift-quiz-preview__all"
          variant="primary"
          size="auto"
          :label="t('gift_quiz.preview.see_all', { count: cards.length })"
          @click="emit('see-all')"
        />
      </div>
    </section>
  </div>
</template>
